<template>
  <div class="store-confirm">
    <!-- 顶部标签栏 (保持不变) -->
    <div class="tabs-container">
      <div v-for="(tab, index) in tabs" :key="index" class="tab-item" :class="{ active: currentTab === tab.id }"
        @click="currentTab = tab.id">
        <span class="tab-text">{{ tab.name }}</span>
        <span v-if="tab.count > 0" class="badge">{{ tab.count }}</span>
        <div v-if="currentTab === tab.id" class="active-line"></div>
      </div>
    </div>

    <div class="order-list">
      <!-- 订单列表循环 -->
      <div v-if="orderList.length > 0">
        <div class="order-card" v-for="item in orderList" :key="item.id" @click="handleClick(item.uuid)">
          <div class="card-header">
            <span class="customer">订单号：{{ item.uuid }}</span>
            <span class="status" :class="getStatusClass(item.status)">{{ getStatusText(item.status) }}</span>
          </div>
          <div class="card-body" v-for="(shop, index) in item.shop_order_infos" :key="index">
            <img :src="comapi.getImageUrl(shop.cover_image)" class="product-img" />
            <div class="product-info">
              <div class="product-title">{{ shop.product_name }}({{ shop.spec_name }})</div>
              <div class="product-specs">规格：{{ shop.unit }}</div>
              <div class="delivery_fee">运费：¥{{ shop.delivery_fee / 100 }}</div>
              <div class="price-row">
                <span class="price">¥{{ shop.pay_amount / 100 }}</span>
                <span class="quantity">x{{ shop.num }}</span>
              </div>
            </div>
          </div>

          <div class="card-footer">
            <span class="order-no"></span>
            <div class="total-wrap">
              实付：¥{{ item.total_money / 100 }}
            </div>
          </div>
        </div>

        <!-- === 新增：底部加载触发区 === -->
        <div class="loading-trigger" ref="loadTrigger">
          <div v-if="isFinished" class="finished-state">
            - 没有更多订单了 -
          </div>
          <div v-else class="idle-state">
            <!-- 这里的空div是为了占位，让Observer能监测到 -->
          </div>
        </div>
      </div>
      <!-- 空状态 -->
      <div v-else-if="!isLoading" class="empty-state">
        <div class="empty-icon">📦</div>
        <div class="empty-text">暂无订单</div>
      </div>
    </div>

    <!-- 全屏加载遮罩 -->
    <div v-if="isLoading && orderList.length === 0" class="fullscreen-loading">
      <div class="loading-content">
        <span class="spinner"></span>
        <div class="loading-text">正在加载中...</div>
      </div>
    </div>
    <div class="height-page"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import storeapi from "@/utils/api/store";
import { useRouter, useRoute } from 'vue-router';
import comapi from "@/utils/api/com";

const router = useRouter();
const route = useRoute();

// 标签栏数据 -1-已取消 0-待支付 1-待接单 2-待发货 3-退款中 4-已完成 5-已退款 6-待发货-部分发货 7-已发货
const tabs = ref([
  { name: '全部', id: 99, count: 0 },
  { name: '待付款', id: 0, count: 0 },
  { name: '待接单', id: 1, count: 0 },
  { name: '待发货', id: 2, count: 0 },
  { name: '待收货', id: 7, count: 0 },
  { name: '已完成', id: 4, count: 0 },
  { name: '退款中', id: 3, count: 0 },
  { name: '已退款', id: 5, count: 0 },
  { name: '已取消', id: -1, count: 0 },
]);

const currentTab = ref(99);

// --- 订单数据与分页状态 ---
const orderList = ref([]);

// === 分页逻辑变量 ===
const isLoading = ref(false); // 是否正在加载
const isFinished = ref(false); // 是否已加载完所有数据
const page = ref(1); // 当前页码
const loadTrigger = ref(null); // DOM引用
let observer = null;

// --- 方法 ---
const getStatusClass = (status) => {
  if (status === 2 || status === 0) return 'status-orange';
  if (status === 4) return 'status-gold';
  return '';
};

// 获取订单状态文本
const getStatusText = (status) => {
  const statusMap = {
    '-1': '已取消',
    '0': '待支付',
    '1': '待接单',
    '2': '待发货',
    '3': '退款中',
    '4': '已完成',
    '5': '已退款',
    '6': '部分发货',
    '7': '已发货'
  };
  return statusMap[status] || status;
};
//订单数量统计
const getordernum = async () => {
  let departmentdata = localStorage.getItem('departmentdata');
  departmentdata = JSON.parse(departmentdata);
  const res = await storeapi.getordernum({
    shop_uuid: departmentdata.department_uuid,
  });
  res.data.forEach(item => {
      tabs.value.find(tab => tab.id === item.status).count = item.count;
  });
};
// 加载订单数据
const fetchOrderList = async (pageNum) => {
  try {
    let departmentdata = localStorage.getItem('departmentdata');
    departmentdata = JSON.parse(departmentdata);
    const res = await storeapi.getorderlist({
      shop_uuid: departmentdata.department_uuid,
      page: pageNum,
      page_size: 10,
      status: currentTab.value === 99 ? null : currentTab.value
    });
    return res.data || [];
  } catch (error) {
    console.error('获取订单列表失败', error);
    return [];
  }
};

// 初始加载数据
const initOrderList = async () => {
  isLoading.value = true;
  isFinished.value = false;
  page.value = 1;
  orderList.value = [];

  try {
    const data = await fetchOrderList(1);
    // 模拟网络延迟，确保遮罩能看到
    await new Promise(resolve => setTimeout(resolve, 500));
    orderList.value = data;
    if (data.length < 10) {
      isFinished.value = true;
    }
  } catch (error) {
    console.error('初始化订单列表失败', error);
  } finally {
    isLoading.value = false;
  }
};

// 加载更多
const loadMore = async () => {
  if (isLoading.value || isFinished.value) return;
  isLoading.value = true;

  try {
    const nextPage = page.value + 1;
    const newItems = await fetchOrderList(nextPage);

    if (newItems.length > 0) {
      orderList.value.push(...newItems);
      page.value = nextPage;
      if (newItems.length < 10) {
        isFinished.value = true;
      }
    } else {
      isFinished.value = true;
    }
  } catch (error) {
    console.error('加载失败', error);
  } finally {
    isLoading.value = false;
  }
};

// ===点击跳转详情 ===
const handleClick = (uuid) => {
  router.push({ path: '/store_order_detail', query: { uuid: uuid } });
};

// 监听标签切换
watch(currentTab, () => {
  initOrderList();
});

// ===生命周期监听 ===
onMounted(() => {
  // 从路由参数获取订单类型
  const orderType = route.query.order_type;
  if (orderType) {
    currentTab.value = Number(orderType);
  }
  // 订单数量统计
  getordernum();

  // 初始加载数据
  initOrderList();

  // 设置交叉观察器
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      loadMore();
    }
  }, {
    threshold: 0.1,
    rootMargin: '50px'
  });

  if (loadTrigger.value) {
    observer.observe(loadTrigger.value);
  }
});

onUnmounted(() => {
  // 销毁监听器
  if (observer) observer.disconnect();
});
</script>

<style lang="less" scoped>
// 变量定义
@color-primary: #333;
@color-secondary: #999;
@color-orange: #FF7123;
@color-gold: #DCBB89;
@color-line: #eee;

.store-confirm {
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif;
  color: @color-primary;
}

.tabs-container {
  display: flex;
  background: #fff;
  overflow-x: auto;
  padding: 0 10px;
  border-bottom: 1px solid transparent;
  position: fixed;
  top: 0; // 确保固定在顶部
  left: 0;
  width: 100%; // 确保占满宽度
  z-index: 10; // 确保在内容之上

  // 隐藏滚动条
  &::-webkit-scrollbar {
    display: none;
  }

  .tab-item {
    position: relative;
    padding: 13px 8px;
    white-space: nowrap;
    font-size: 15px;
    color: #999;
    cursor: pointer;

    &.active {
      color: #DCBB89;
      font-weight: 600;
    }

    .badge {
      position: absolute;
      top: 4px;
      right: 0px;
      background-color: @color-orange;
      color: #fff;
      font-size: 10px;
      height: 14px;
      min-width: 14px;
      line-height: 14px;
      text-align: center;
      border-radius: 7px;
      padding: 0 3px;
    }

    .active-line {
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: 20px;
      height: 3px;
      background-color: #dcb36d;
      border-radius: 2px;
    }
  }
}

// 3. 订单列表样式
.order-list {
  padding: 12px;
  margin-top: 50px; // 留出 Header 的位置

  .order-card {
    background: #fff;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

    // 头部：客户与状态
    .card-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      font-size: 13px;

      .customer {
        color: #888;
      }

      .status {
        font-weight: 500;
      }

      .status-orange {
        color: @color-orange;
      }

      .status-gold {
        color: @color-gold;
      }
    }

    .card-body {
      display: flex;
      margin-bottom: 15px;

      .product-img {
        width: 80px;
        height: 80px;
        border-radius: 8px;
        object-fit: cover;
        margin-right: 12px;
        background-color: #eee;
      }

      .product-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        .product-title {
          font-size: 15px;
          color: #333;
          font-weight: 500;
          line-height: 1.4;
        }

        .product-specs {
          font-size: 12px;
          color: @color-secondary;
          margin-top: 4px;
        }

        .delivery_fee {
          font-size: 10px;
          color: #FF7123;
        }

        .price-row {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .price {
            font-size: 16px;
            color: @color-orange;
            font-weight: 600;
          }

          .quantity {
            font-size: 14px;
            color: @color-secondary;
          }
        }
      }
    }

    .card-footer {
      border-top: 1px solid #f7f7f7;
      padding-top: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 13px;
      color: #666;

      .total-wrap {
        display: flex;
        align-items: center;
        color: #333;
        font-size: 15px;
        font-weight: bold;
      }
    }
  }

  // === 新增：底部 Loading 区域样式 ===
  .loading-trigger {
    text-align: center;
    padding: 15px 0;
    color: #999;
    font-size: 13px;
    min-height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;

    .loading-state {
      display: flex;
      align-items: center;

      // 加载圈圈动画
      .spinner {
        width: 14px;
        height: 14px;
        border: 2px solid #ccc;
        border-top-color: @color-orange;
        border-radius: 50%;
        margin-right: 8px;
        animation: spin 0.8s linear infinite;
      }
    }

    .finished-state {
      color: #ccc;
    }
  }

  // 空状态
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    text-align: center;

    .empty-icon {
      font-size: 64px;
      margin-bottom: 16px;
    }

    .empty-text {
      font-size: 16px;
      color: #999;
    }
  }

  // 加载中状态
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 0;
    color: #999;
    font-size: 13px;

    .spinner {
      width: 14px;
      height: 14px;
      border: 2px solid #ccc;
      border-top-color: @color-orange;
      border-radius: 50%;
      margin-right: 8px;
      animation: spin 0.8s linear infinite;
    }
  }
}

.height-page {
  height: 60px;
}

// 全屏加载遮罩
.fullscreen-loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: all;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .spinner {
      width: 32px;
      height: 32px;
      border: 3px solid #f3f3f3;
      border-top: 3px solid @color-orange;
      border-radius: 50%;
      margin-bottom: 16px;
      animation: spin 1s linear infinite;
    }

    .loading-text {
      font-size: 16px;
      color: #666;
    }
  }
}

// 旋转动画关键帧
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>